import React, { useCallback, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import DialogHeader from './DialogHeader';

/**
 * Egységes megerősítő ablak a böngésző natív `window.confirm` helyett.
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Törlés', message: 'Biztosan?', confirmLabel: 'Törlés', color: 'error' }))) return;
 *   ...
 *   return <>{...}{confirmDialog}</>;
 *
 * Opciók: title, message (string vagy ReactNode), confirmLabel, cancelLabel,
 * color (MUI gombszín, alap 'primary'), icon (DialogHeader ikon).
 */
export function useConfirm() {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setOptions(opts);
  }), []);

  const close = (result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  };

  const dialog = (
    <Dialog open={Boolean(options)} onClose={() => close(false)} maxWidth="xs" fullWidth>
      {options && (
        <>
          <DialogTitle sx={{ p: 0 }}>
            <DialogHeader icon={options.icon || <HelpOutline />} title={options.title || 'Megerősítés'} />
          </DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            {typeof options.message === 'string'
              ? <Typography sx={{ whiteSpace: 'pre-line' }}>{options.message}</Typography>
              : options.message}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
            <Button onClick={() => close(false)}>{options.cancelLabel || 'Mégse'}</Button>
            <Button variant="contained" color={options.color || 'primary'} onClick={() => close(true)} autoFocus>
              {options.confirmLabel || 'Rendben'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return [confirm, dialog];
}

export default useConfirm;
